import { z } from "zod";
import { collapseWhitespace, looksMisencoded } from "../text.js";
/** A replacement character means the bytes were already wrong on arrival. */
const MISENCODED = "This text arrived damaged; check the encoding of what sent it.";
/**
 * Where an acquisition came from, written in the cellar's own words.
 *
 * A closed enum of five until `9.11` — Systembolaget, the producer, an
 * auction, a gift, and `Other` — which meant that a bottle bought through
 * Vivino or carried home from a restaurant had to be filed as *Annat*. The
 * schema comment had predicted exactly that failure, and the import proved it
 * by filing 112 of 125 lots as `Other`.
 *
 * So it travels as a name, like the producer and the region beside it: the
 * server resolves or creates the per-cellar reference row through the same
 * normaliser the picker matched with, and an existing spelling wins.
 */
const writtenSource = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().min(1).max(120))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED });
/**
 * The wine's article number at Systembolaget — `9362801` — carried by both lot
 * forms, registering and correcting, because both are where the receipt it is
 * read off lies open. One definition, so the two cannot come to disagree about
 * what a number is.
 *
 * **Written to the wine, not to the lot.** An article number names what could
 * be bought again, which is what the wine is; `project-scope.md` has the
 * decision and what it gives up. Text rather than a number, because it is an
 * identifier: nobody adds two of them, and a leading nought is part of one.
 *
 * Optional in the sense a request uses the word: absent leaves the wine's
 * number alone, null clears it. The forms send it only when it was touched, so
 * an older browser, and a lot saved without looking at the field, change
 * nothing — which matters most on the correcting form, opened long after the
 * purchase to fix a price.
 */
const systembolagetId = z
    .string()
    .trim()
    .pipe(z.string().max(40))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional();
/**
 * Format belongs to the bottle, not the wine: the same wine is routinely owned
 * in two sizes, and a magnum ages differently. A lot is one acquisition at one
 * price, so its bottles share a format; two sizes are two lots.
 */
export const bottleFormatSchema = z.enum([
    "Half",
    "Bottle",
    "Magnum",
    "Jeroboam",
    "Other",
]);
export const bottleStatusSchema = z.enum([
    "InCellar",
    "Away",
    "Consumed",
    "Gifted",
    "Sold",
    "Lost",
    "Broken",
]);
const dateOnly = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null);
const freeText = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().max(500))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null);
/** ISO 4217, uppercase. */
const currency = z
    .string()
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{3}$/))
    .nullable()
    .default(null);
export const createLotRequestSchema = z
    .object({
    /**
     * Nullable because two rows of the sheet hold free text where a date
     * should be. An invented date would be worse than an absent one.
     */
    purchasedOn: dateOnly,
    pricePerBottle: z.number().min(0).max(1_000_000).nullable().default(null),
    currency,
    quantity: z.number().int().positive().max(240),
    source: writtenSource,
    note: freeText,
    /**
     * How the bottle got here, as against how it tasted. Known once, when the
     * bottles are registered, and true of the whole acquisition — which is why
     * they live on the lot rather than on a bottle or in a tasting note.
     *
     * These two are readable by guests while the rest of the lot is not:
     * hiding what a bottle cost is tact, hiding who gave it is the opposite,
     * since the person reading may well be the person who gave it.
     */
    receivedFrom: freeText,
    promptedBy: freeText,
    /** Asked for here because the receipt is in hand while a lot is
     *  registered; see `systembolagetId` above for what it is and where it
     *  goes. */
    systembolagetId,
    /** Where the bottles are put, and what size they are. */
    format: bottleFormatSchema.default("Bottle"),
    /**
     * Null only for a lot that was already drunk, which never stood anywhere
     * this application knew about. Every other lot needs a shelf: bottles that
     * exist are somewhere, and a nullable shelf on the ordinary path would
     * make "in the cellar, nowhere in particular" an easy accident.
     */
    shelfId: z.string().min(1).nullable().default(null),
    /**
     * Bottles finished before this application knew about them.
     *
     * A cellar has a past. Wines bought and drunk before any of this existed
     * are worth a row — *have I had this before* is one of the questions the
     * record is kept for — but a wine with no bottles at all answers only
     * that, and nothing about when, how many, or what it cost. So the lot is
     * registered as it happened and its bottles are created already consumed:
     * the same shape a bottle drunk through the interface ends up in, and the
     * shape the import needs, since the spreadsheet counts 197 bottles against
     * 158 still standing.
     *
     * The date is required rather than inferred when this is used at all. An
     * approximate year typed by the person who was there is worth something;
     * a date this code picked because the field was empty would be a fact
     * nobody asserted, and would read as one in fifteen years.
     */
    consumedOn: dateOnly,
})
    /**
     * Bottles that are still here have to be somewhere. The shelf is optional in
     * the shape above only for the lot that was drunk before it was recorded.
     */
    .refine((lot) => lot.consumedOn !== null || lot.shelfId !== null, {
    message: "Bottles that are still in the cellar need a shelf.",
    path: ["shelfId"],
})
    /** A bottle cannot have been drunk before it was bought. */
    .refine((lot) => lot.consumedOn === null ||
    lot.purchasedOn === null ||
    lot.consumedOn >= lot.purchasedOn, {
    message: "A bottle cannot be drunk before it is bought.",
    path: ["consumedOn"],
})
    /**
     * A price and its currency stand or fall together, which is the scope's
     * argument about rating scales applied to money: 450 is not a price unless
     * something says whether it is kronor or euros. A gift has neither. The
     * database refuses the mismatch too; refusing it here means the form can say
     * so at the field instead of relaying a 400.
     */
    .refine((lot) => (lot.pricePerBottle === null) === (lot.currency === null), {
    message: "A price needs its currency, and a currency needs its price.",
    path: ["currency"],
});
/**
 * Correcting a lot, which is not the same act as registering one.
 *
 * Only the lot's own facts are here: what it cost, when it was bought, where it
 * came from, who gave it and what prompted it. None of those asserts that
 * anything happened — together they describe one acquisition — so
 * `project-scope.md` allows them to be corrected at any time, including long
 * after the bottles have been moved or drunk. A wrong price is a wrong price
 * whether or not a bottle has since been opened. The one exception is the
 * wine's article number, which rides along for the reason it rides along on
 * the create form: the receipt it is read off is what this form is opened to
 * copy from.
 *
 * **Quantity, format and shelf are deliberately absent.** Those are facts about
 * bottles, and bottles are moved, drunk and given away through their own
 * operations, which leave events behind. Editing a quantity here would create
 * or destroy physical objects in silence, which is the one thing the whole
 * bottle model exists to prevent.
 */
export const updateLotRequestSchema = z
    .object({
    purchasedOn: dateOnly,
    pricePerBottle: z.number().min(0).max(1_000_000).nullable().default(null),
    currency,
    source: writtenSource,
    note: freeText,
    receivedFrom: freeText,
    promptedBy: freeText,
    /** The wine's, not the lot's — the one field here that is not the lot's
     *  own fact, and on this form only because the receipt is. */
    systembolagetId,
})
    /** The same pairing the create path enforces: 450 is not a price unless
     *  something says whether it is kronor or euros. */
    .refine((lot) => (lot.pricePerBottle === null) === (lot.currency === null), {
    message: "A price needs its currency, and a currency needs its price.",
    path: ["currency"],
});
export const lotSummarySchema = z.object({
    id: z.string(),
    /**
     * How the bottles were acquired — and `null` for a reader who may not be
     * told.
     *
     * Since `9.2` these five fields carry two different silences, and the screen
     * does not have to tell them apart: a lot that recorded no price sends
     * `null`, and so does a lot whose price this reader may not see. The screen
     * says *not shown* from the cellar's `seesPrices` and *nothing recorded*
     * from the field, which is the only place the two are distinguished.
     *
     * `source` is nullable for that reason alone — every lot has one. It is
     * withheld with the price rather than with the provenance below because it
     * answers where the bottles were bought, which is the question the price is
     * part of; see the decision in `project-scope.md`. Since `9.11` it is the
     * name on the cellar's own reference row rather than one of five
     * identifiers, so nothing translates it: it is what its owner typed.
     */
    purchasedOn: z.string().nullable(),
    pricePerBottle: z.number().nullable(),
    currency: z.string().nullable(),
    quantity: z.number().int(),
    source: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * The provenance, which every reader sees. Hiding what a bottle cost is
     * tact; hiding who gave it is the opposite, since the person reading may
     * well be the person who gave it.
     */
    receivedFrom: z.string().nullable(),
    promptedBy: z.string().nullable(),
    /**
     * Whether this lot can still be deleted outright.
     *
     * A lot registered by mistake has no other way back — bottles are never
     * deleted, so the alternative is marking twelve of them lost, which lies in
     * the history permanently. project-scope.md therefore allows the delete for
     * exactly as long as the lot is still a typing mistake: nothing has been
     * moved, drunk, given away or confirmed at a stocktake. After that it is a
     * real acquisition with a real history and the door closes.
     *
     * Sent rather than worked out by the client, because the client sees a
     * bottle's status and not its events — and a bottle moved to another shelf
     * still reads as standing in the cellar.
     */
    untouched: z.boolean(),
});
/**
 * The moment a bottle left the cellar, as the wine's page reads it.
 *
 * The date and the note are what somebody typed, not the event itself, and
 * `project-scope.md` allows both to be corrected in place: a bottle entered as
 * the 14th when it was the 4th did not leave on the 14th, and two events to fix
 * one typing mistake would be a worse history than the one being corrected.
 * That it *left* is still undone only by the compensating event.
 */
export const departureSchema = z.object({
    eventId: z.string(),
    /** The same four endings the status has, plus `Consumed`. */
    type: bottleStatusSchema,
    occurredAt: z.string(),
    /** `to Anna, for the christening` — true of the moment, not of the object.
     *  A drunk bottle keeps its words in a tasting note instead. */
    note: z.string().nullable(),
});
export const bottleSummarySchema = z.object({
    id: z.string(),
    format: bottleFormatSchema,
    status: bottleStatusSchema,
    shelfName: z.string().nullable(),
    storageName: z.string().nullable(),
    /**
     * Where it went and where it will go back to, while it is `Away`. Null for
     * every other status, and `awayFromShelfName` is null as well for a bottle
     * whose shelf was removed while it was out — which is the one case where
     * returning it has to ask.
     */
    awayNote: z.string().nullable(),
    /**
     * Who carried it out, and on what day — for a bottle that is out.
     *
     * Added at the same time as the contributor role, because that is when it
     * started mattering: a cellar where only its owner takes bottles out needs
     * no byline on the act, and one where somebody else does needs one on every
     * bottle that is missing from a shelf. The event has always carried the
     * actor; nothing ever showed it.
     *
     * **The day it left, not the minute it was typed.** `BottleEvent.occurredAt`
     * is when it happened in the world and says so on the column; a bottle
     * carried to a dinner on Saturday and recorded on Sunday left on Saturday.
     * A clock time here would be the moment a form was submitted wearing the
     * authority of the moment a cork left the cellar.
     *
     * `awayBy` is null for an act the import performed or one whose account has
     * gone — the history outlives both. Both are null for every bottle that is
     * not away.
     *
     * Nullish for one release, per CLAUDE.md: the browser deploys on a green
     * `main` and the API by hand, and a row that simply says less is the right
     * thing for an older API to produce.
     *
     * **That release ended with revision 44 on 2026-09-21 and the leniency came
     * off on the 22nd.** Both stay *nullable*, which is the distinction that
     * matters: null here is a real answer — a bottle that is not away, or one
     * carried out by the import or by an account since gone — while a response
     * with no `awayBy` field at all is a broken deploy.
     */
    awayBy: z.string().nullable(),
    awayOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    awayFromShelfName: z.string().nullable(),
    awayFromStorageName: z.string().nullable(),
    /**
     * How this bottle left, for one that has.
     *
     * Null while it is still owned — and also null for a bottle that left on a
     * day nobody wrote down, which is 35 of the ones the import brought in.
     * `BottleEvent.occurredAt` is not nullable, so those were written with a
     * status and no event, and this says exactly that rather than inventing a
     * date to fill the shape. A bottle whose departure is null and whose status
     * is not `InCellar` or `Away` is one that went, on a day nobody knows.
     */
    departure: departureSchema.nullable(),
});
//# sourceMappingURL=lot.js.map